import { APP_INITIALIZER, EnvironmentProviders, inject, makeEnvironmentProviders } from '@angular/core';
import { AbIfService } from '@df/module-ab/ab-if.service';
import { abIfExpField, abIfExpSection } from '@df/module-ab/cms2/ab-cms2';
import { TimeoutService } from '@df/ng/timeout.service';
import { ICmsDirective } from '@icms/client/assets/text/icms.directive';
import {
    ICmsContentComponentValidationService,
    IValidtionEvent
} from '@icms/core/content/component/icms-content-component-validation.service';
import { ICmsDirectiveSanitationService, ISanitationEvent } from '@icms/core/content/directive/icms-directive.sanitation.service';
import { ICmsContentService } from '@icms/core/content/icms-content.service';
import { NICmsContentComponent } from '@icms/core/content/interfaces/icms-content-component.interfaces';
import { Subscription } from 'rxjs';
import { AbService } from '../ab.service';
import './ab-cms2-config';
import { AbCms2ShortcutsProvider } from './shortcuts/ab-cms2-shortcuts.provider';

interface IComponentStyles extends NICmsContentComponent.IComponentStyles {
    abIfExp: string;
}

export const providers = (options: { componentHideOption: boolean; binding: boolean } = { componentHideOption: true, binding: true }) => [
    AbCms2ShortcutsProvider,
    {
        provide: APP_INITIALIZER,
        useFactory:
            (
                abService: AbService,
                abIfService: AbIfService,
                timeoutService: TimeoutService,
                iCmsContentComponentValidationService: ICmsContentComponentValidationService,
                iCmsDirectiveSanitationService: ICmsDirectiveSanitationService
            ) =>
            () => {
                if (options.componentHideOption) {
                    ICmsContentService.registerComponentMetaPatch((meta: NICmsContentComponent.IMeta) => {
                        // styles are only for non-block components
                        if (!meta.block) {
                            const firstStyleFieldIndex =
                                meta.fields.findIndex(field => field.key.startsWith('styles.')) || meta.fields.length - 1;
                            meta.fields.splice(firstStyleFieldIndex, 0, abIfExpSection, abIfExpField);
                        }
                    });

                    const dataObjects = new Map<
                        NICmsContentComponent.IData,
                        {
                            stateSubscription: Subscription;
                            abValue: string;
                        }
                    >();

                    iCmsContentComponentValidationService.validation$.subscribe((event: IValidtionEvent) => {
                        const data = event.data as NICmsContentComponent.IDataWithStyles;
                        const abValue = (data?.styles as IComponentStyles)?.abIfExp;
                        const dataObject = dataObjects.get(data);
                        if (dataObject && dataObject.abValue !== abValue) {
                            dataObject.stateSubscription.unsubscribe();
                            dataObjects.delete(data);
                        }
                        if (abValue) {
                            const state$ = abIfService.getState(abValue);
                            if (!dataObject) {
                                let lastState = state$.value;
                                const stateSubscription = state$.subscribe(state => {
                                    if (lastState !== state) {
                                        lastState = state;
                                        iCmsContentComponentValidationService.getObjectHook(data, true)?.trigger$.next();
                                    }
                                });
                                dataObjects.set(data, {
                                    stateSubscription,
                                    abValue
                                });
                            }

                            if (!state$.value) {
                                event.error = {
                                    copy: 'Not allowed for current experiment variant',
                                    level: 'error'
                                };
                            }
                        }
                    });
                }

                if (options.binding) {
                    // iCmsDirective binding watch
                    const iCmsDirectiveContentExpIds: string[] = [];
                    iCmsDirectiveSanitationService.sanitation$.subscribe((event: ISanitationEvent) => {
                        if (event.content === 'html') {
                            // match tags like <if-exp [experimentName]="variant">
                            const regex = new RegExp('<if-exp[^>]+>(.*?)</if-exp>', 'gm');
                            event.copy = event.copy.replace(regex, (input, content) => {
                                const match = input.match(/<if-exp\s+([\w\d-]+)\s*=\s*['"]?([\w]+)?['"]?\s*>([\s\S.]*)<\/if-exp>/m);
                                if (match) {
                                    const expId = match[1];
                                    const variant = match[2].trim();
                                    const invert = variant[0] === '!';
                                    if (!iCmsDirectiveContentExpIds.includes(expId)) {
                                        iCmsDirectiveContentExpIds.push(expId);
                                    }
                                    const experimentVariant = abService.getVariant(expId);
                                    if (invert ? experimentVariant !== variant.substring(1) : experimentVariant === variant) {
                                        return content;
                                    }
                                }
                                return '';
                            });
                        }
                    });

                    // trigger iCmsDirective update on variant change if needed
                    const triggerUpdate = timeoutService.debounce(() => {
                        ICmsDirective.triggerMassUpdate$.next();
                    }, 100);

                    abService.change$.subscribe((event: { expId: string; variant: string }) => {
                        if (iCmsDirectiveContentExpIds.includes(event.expId)) {
                            triggerUpdate();
                        }
                    });
                }
            },
        deps: [AbService, AbIfService, TimeoutService, ICmsContentComponentValidationService, ICmsDirectiveSanitationService],
        multi: true
    }
];

/**
 * @usage Angular 18+
 *
 * ```ts
 * export const appConfig: ApplicationConfig = {
 *   providers: [
 *     provideDfModuleAb(),
 *     provideDfModuleAbCms2()
 *   ]
 * };
 * ```
 */
export function provideDfModuleAbCms2(
    options: { componentHideOption: boolean; binding: boolean } = { componentHideOption: true, binding: true }
): EnvironmentProviders {
    return makeEnvironmentProviders([
        ...providers(options),
        {
            provide: APP_INITIALIZER,
            useValue() {
                inject(AbCms2ShortcutsProvider).appInitialize();
            },
            multi: true
        }
    ]);
}
